import React, { Component } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Button } from 'controls'
import { TentativeParentsActionCreators } from 'app/reducers/TentativeParentsRedux'
import {
  getTentativeParents,
  getTentativeParentsLoading,
  getTentativeParentsError,
  getShowParentErrorModal,
  getShowParentConfirmModal,
  getSelectedParentForConfirmation,
} from 'app/selectors/TentativeParentsSelectors'
import ParentSearcher from './ParentSearcher'
import { ParentErrorModal, ParentConfirmModal } from 'components/Modals'
import styles from './styles'

class ParentSelector extends Component {
  constructor(props) {
    super(props)
    this.state = {
      selectingParentId: null // Track which parent is being selected
    }
  }

  componentDidMount() {
    // Load tentative parents when component mounts
    this.props.loadTentativeParents()
  }

  selectParent = parent => {
    // Show confirmation modal instead of directly selecting
    this.props.openParentConfirmModal(parent)
  }

  selectParentFromSearch = searchResult => {
    // Show confirmation modal for search results too
    this.props.openParentConfirmModal(searchResult)
  }

  deleteParent = parent => {
    // Use the parent.id from the nested structure
    const parentId = parent.parent && parent.parent.id
    if (parentId) {
      this.props.removeParent(parentId)
      if (this.props.onParentDeleted) {
        this.props.onParentDeleted(parent)
      }
    }
  }

  closeParentErrorModal = () => {
    this.props.closeParentErrorModal()
  }

  handleConfirmParentSelection = () => {
    const selectedParent = this.props.selectedParentForConfirmation
    if (selectedParent) {
      // Get the parent ID from either structure
      const parentId = selectedParent.parent?.id || selectedParent.id
      if (parentId) {
        // Set loading state for this specific parent
        this.setState({ selectingParentId: parentId })
        
        // Dispatch the confirmation action which will trigger the actual selection
        this.props.confirmParentSelection()
        this.props.chooseParent(parentId)
        
        if (this.props.onParentSelected) {
          this.props.onParentSelected(selectedParent)
        }
      }
    }
  }

  handleCancelParentSelection = () => {
    this.props.hideParentConfirmModal()
  }

  componentDidUpdate(prevProps) {
    // Clear loading state when operation finishes (for error cases)
    // Note: Success cases will reload the page, so this mainly handles errors
    if (prevProps.isLoading && !this.props.isLoading) {
      this.setState({ selectingParentId: null })
    }
  }

  render() {
    const { 
      tentativeParents, 
      isLoading, 
      error, 
      showParentConfirmModal,
      selectedParentForConfirmation,
      subtitleComponent
    } = this.props
    const { selectingParentId } = this.state

    const title = subtitleComponent || (
      <p className="title" style={{ fontWeight: 'bold' }}>
        Elige a tu padrino y potencia tu crecimiento en iad
      </p>
    )

    // Get parent name for confirmation modal
    const getParentName = (parent) => {
      if (!parent) return 'este asesor'
      const user = parent.parent?.user || parent.user
      if (user) {
        return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'este asesor'
      }
      return 'este asesor'
    }

    if (isLoading) {
      return (
        <div className="parent-selector">
          <div className="loading">Cargando padrinos...</div>
          <style jsx>{styles}</style>
        </div>
      )
    }

    if (error && !this.props.showParentErrorModal) {
      return (
        <div className="parent-selector">
          <div className="error">
            Error al cargar los padrinos.
            <button onClick={() => this.props.loadTentativeParents()}>
              Reintentar
            </button>
          </div>
          <style jsx>{styles}</style>
        </div>
      )
    }

    return (
      <div className="parent-selector">
        <div className="selector-leyend">
          {title}
          <p className="subtitle">
            Selecciona al asesor experto en tu área, que refleje los valores de
            iad: profesional, confiable, humilde, unido, valiente, sabio, leal y
            colaborativo, y crece acompañado en cada paso para lograr tu éxito.
          </p>
        </div>
        <ParentSearcher onParentSelected={this.selectParentFromSearch} />

        <div className="suggested-parents">
          <h6>Opciones sugeridas:</h6>
          {tentativeParents.length === 0 ? (
            <div className="no-results">
              No hay sugerencias de padrinos disponibles en este momento.
            </div>
          ) : (
            tentativeParents.map(parent => {
              const user = parent.parent?.user
              const fullName = user
                ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                : 'N/A'

              return (
                <div key={parent.id} className="parent-option">
                  <Button
                    size="small"
                    onClick={() => this.deleteParent(parent)}
                    className="delete-button"
                    title="Eliminar padrino"
                  >
                    ✕
                  </Button>
                  <div className="parent-info">
                    <div className="parent-name">{fullName}</div>
                    <div className="parent-email">{user?.email || 'N/A'}</div>
                    <div className="parent-phone">
                      {user?.telephone || 'N/A'}
                    </div>
                  </div>
                  <div className="parent-actions">
                    <Button
                      color="primary"
                      size="small"
                      loading={selectingParentId === (parent.parent?.id || parent.id)}
                      onClick={() => this.selectParent(parent)}
                    >
                      Elegir
                    </Button>
                  </div>
                </div>
              )
            })
          )}
        </div>
        <ParentErrorModal
          isOpen={this.props.showParentErrorModal || false}
          onClose={this.closeParentErrorModal}
        />
        <ParentConfirmModal
          isOpen={showParentConfirmModal || false}
          onConfirm={this.handleConfirmParentSelection}
          onCancel={this.handleCancelParentSelection}
          parentName={getParentName(selectedParentForConfirmation)}
          loading={selectingParentId !== null}
        />
        <style jsx>{styles}</style>
      </div>
    )
  }
}

ParentSelector.propTypes = {
  // Redux props
  tentativeParents: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  loadTentativeParents: PropTypes.func.isRequired,
  removeParent: PropTypes.func.isRequired,
  chooseParent: PropTypes.func.isRequired,
  showParentErrorModal: PropTypes.bool,
  closeParentErrorModal: PropTypes.func.isRequired,
  showParentConfirmModal: PropTypes.bool,
  selectedParentForConfirmation: PropTypes.object,
  openParentConfirmModal: PropTypes.func.isRequired,
  hideParentConfirmModal: PropTypes.func.isRequired,
  confirmParentSelection: PropTypes.func.isRequired,
  // Callback props
  onParentSelected: PropTypes.func,
  onParentDeleted: PropTypes.func,
  subtitleComponent: PropTypes.node,
}

ParentSelector.defaultProps = {
  onParentSelected: null,
  onParentDeleted: null,
  showParentErrorModal: false,
  showParentConfirmModal: false,
  selectedParentForConfirmation: null,
  subtitleComponent: null,
}

const mapStateToProps = state => ({
  tentativeParents: getTentativeParents(state),
  isLoading: getTentativeParentsLoading(state),
  error: getTentativeParentsError(state),
  showParentErrorModal: getShowParentErrorModal(state),
  showParentConfirmModal: getShowParentConfirmModal(state),
  selectedParentForConfirmation: getSelectedParentForConfirmation(state),
})

const mapDispatchToProps = dispatch => ({
  loadTentativeParents: params =>
    dispatch(TentativeParentsActionCreators.all(params)),
  removeParent: agentId =>
    dispatch(TentativeParentsActionCreators.remove(agentId)),
  chooseParent: agentId =>
    dispatch(TentativeParentsActionCreators.choose(agentId)),
  closeParentErrorModal: () =>
    dispatch(TentativeParentsActionCreators.closeParentErrorModal()),
  openParentConfirmModal: parent =>
    dispatch(TentativeParentsActionCreators.showParentConfirmModal(parent)),
  hideParentConfirmModal: () =>
    dispatch(TentativeParentsActionCreators.hideParentConfirmModal()),
  confirmParentSelection: () =>
    dispatch(TentativeParentsActionCreators.confirmParentSelection()),
})

export default connect(mapStateToProps, mapDispatchToProps)(ParentSelector)
